'use client';

import { useState } from 'react';
import { Globe, ArrowLeft, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { trackUserAction } from '@/app/actions/posthog';
import { PostHogEventName } from '@/types/posthog';
import { useOnboardingStore } from '../../store';
import { updateCompanyOnboarding } from '../../actions';
import { StepWrapper } from '../shared/StepWrapper';

interface BrandStepProps {
  onNext: () => void;
  onBack: () => void;
  onShopUrlSubmit: (url: string) => void;
  isInvitedUser: boolean;
}

function getShopUrlCopy(userType: string | null, isInvitedUser: boolean) {
  if (isInvitedUser) {
    return {
      label: 'Your shop URL',
      placeholder: 'https://yourstore.com',
      subtitle: "We'll use it to personalize your forms and branding.",
    };
  }

  switch (userType) {
    case 'agency':
      return {
        label: "Want to try it with a client's brand?",
        placeholder: 'https://clientstore.com',
        subtitle: "Paste any eCommerce store URL and we'll generate a demo email with their products and branding.",
      };
    case 'brand':
      return {
        label: 'Drop your Shop URL',
        placeholder: 'https://yourstore.com',
        subtitle: "We'll use it to customize your forms. Plus, we'll send you a demo email with your products and branding in under 60 seconds.",
      };
    default:
      return {
        label: 'Store URL (optional)',
        placeholder: 'https://anystore.com',
        subtitle: 'Paste any eCommerce store URL to see Kinetic in action.',
      };
  }
}

export function BrandStep({ onNext, onBack, onShopUrlSubmit, isInvitedUser }: BrandStepProps) {
  const store = useOnboardingStore();
  const [brandName, setBrandName] = useState(store.brandName);
  const [shopUrl, setShopUrl] = useState(store.shopUrl);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  const showBrandName = !isInvitedUser;
  const shopCopy = getShopUrlCopy(store.userType, isInvitedUser);

  const handleContinue = async () => {
    setIsSubmitting(true);
    setUrlError(null);

    // Save brand name to company
    if (brandName && store.companyId) {
      await updateCompanyOnboarding(store.companyId, { brandName });
    }

    store.setBrandData({ brandName, shopUrl });

    // Trigger AI pipeline if URL provided
    if (shopUrl.trim()) {
      // Basic URL validation
      let urlToExtract = shopUrl.trim();
      if (!urlToExtract.startsWith('http://') && !urlToExtract.startsWith('https://')) {
        urlToExtract = 'https://' + urlToExtract;
      }

      try {
        new URL(urlToExtract);
      } catch {
        setUrlError('Please enter a valid URL');
        setIsSubmitting(false);
        return;
      }

      onShopUrlSubmit(urlToExtract);

      // Track shop URL submission
      if (store.companyId) {
        trackUserAction(
          store.email,
          store.companyId,
          PostHogEventName.ONBOARDING_WIZARD_SHOP_URL_SUBMITTED,
          { shop_url: urlToExtract }
        );
      }
    }

    setIsSubmitting(false);
    onNext();
  };

  const handleSkipUrl = () => {
    if (brandName && store.companyId) {
      updateCompanyOnboarding(store.companyId, { brandName });
    }
    store.setBrandData({ brandName, shopUrl: '' });
    onNext();
  };

  const title = isInvitedUser
    ? 'Tell us about your store'
    : store.userType === 'agency'
      ? "Tell us about your agency"
      : "Tell us about your brand";

  return (
    <StepWrapper title={title}>
      <div className="space-y-5">
        {showBrandName && (
          <div className="space-y-1.5">
            <Label htmlFor="brandName">
              {store.userType === 'agency' ? "What's your agency called?" : "What's your brand called?"}
            </Label>
            <Input
              id="brandName"
              placeholder={store.userType === 'agency' ? 'Acme Agency' : 'Acme Store'}
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              autoFocus
            />
          </div>
        )}

        {store.userType !== 'exploring' && (
          <div className="space-y-1.5">
            <Label htmlFor="shopUrl">{shopCopy.label}</Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="shopUrl"
                placeholder={shopCopy.placeholder}
                value={shopUrl}
                onChange={(e) => {
                  setShopUrl(e.target.value);
                  setUrlError(null);
                }}
                className="pl-9"
                autoFocus={isInvitedUser}
              />
            </div>
            <p className="text-xs text-muted-foreground">{shopCopy.subtitle}</p>
            {urlError && <p className="text-xs text-red-500">{urlError}</p>}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            onClick={handleContinue}
            className="w-full bg-primary hover:bg-primary/90 rounded-[13px] h-12 text-base"
            disabled={isSubmitting || (showBrandName && !brandName.trim())}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
          </Button>

          {store.userType !== 'exploring' && shopUrl.trim() === '' && (
            <button
              onClick={handleSkipUrl}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip for now
            </button>
          )}
        </div>

        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
      </div>
    </StepWrapper>
  );
}
